import { useQuery } from "@apollo/client";
import { isEmpty } from "lodash";
import { FC, useEffect, useMemo, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { FreeMode, Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import ModalCarousel from "~/components/ImagesReviewCarousel/ModalCarousel";
import { QUERY_GET_ALL_REVIEWS_ONLY_IMG } from "~/data/home";
import { ReviewItem } from "~/data/product/interfaces";

import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/pagination";

type ImagesReviewSliderProps = {
  activeTab?: string;
};

const ImagesReviewSlider: FC<ImagesReviewSliderProps> = () => {
  const [page, setPage] = useState(1);
  const [current, setCurrent] = useState(0);

  const [open, setOpen] = useState(false);
  const [initialSlide, setInitialSlide] = useState(0);

  const { data, loading, fetchMore, networkStatus } = useQuery(
    QUERY_GET_ALL_REVIEWS_ONLY_IMG,
    {
      variables: {
        pageSize: 3,
        images: true,
      },
      context: { fetchOptions: { method: "GET" } },
      notifyOnNetworkStatusChange: true,
    }
  );

  const items: ReviewItem[] = useMemo(() => {
    return data?.allReview?.items || [];
  }, [data?.allReview?.items]);

  const images = useMemo(() => {
    if (!items.length) return [];
    return items.map((item) => item?.images).flat();
  }, [items]);

  const page_info = data?.allReview?.page_info;

  const handleShowMore = () => {
    if (networkStatus === 3) return;
    if (
      Number(Number(page_info?.current_page) * page) >=
      Number(page_info?.total_pages)
    )
      return;
    if (!!fetchMore) {
      const increasePage = page ? Number(page) + 1 : 1;
      setPage(increasePage);
      fetchMore({
        variables: {
          currentPage: increasePage,
        },
        updateQuery: (previousResult: any, { fetchMoreResult }: any) => {
          if (!fetchMoreResult) return previousResult;
          const newReviews = fetchMoreResult?.allReview?.items || [];
          const nextReview = [
            ...previousResult?.allReview?.items,
            ...newReviews,
          ];

          return {
            allReview: {
              ...previousResult.allReview,
              items: nextReview,
            },
          };
        },
      });
    }
  };

  const imgLength = images.length;
  const defaultSlidesToShow = 4;
  const defaultSlidesToScroll = 4;

  useEffect(() => {
    if (!images.length || page_info?.current_page >= page_info?.total_pages)
      return;

    const numberImgShowed =
      current * defaultSlidesToScroll + defaultSlidesToShow + 1;
    const hasFetchMore =
      imgLength <=
      numberImgShowed + defaultSlidesToScroll * defaultSlidesToScroll * 2;

    if (hasFetchMore) {
      handleShowMore();
    }
  }, [imgLength, current, page_info]);

  const loadingReview = loading || networkStatus === 1;

  return (
    <div>
      {!!images.length || loadingReview ? (
        <div>
          <p className="my-2 font-semibold">Photos from reviews</p>
          <Swiper
            spaceBetween={10}
            slidesPerView={2.5}
            onSlideChange={(swiper) => setCurrent(swiper.activeIndex)}
            modules={[FreeMode, Navigation]}
            navigation={{
              enabled: false,
            }}
            breakpoints={{
              540: {
                spaceBetween: 10,
                slidesPerView: 3.5,
              },
              640: {
                spaceBetween: 10,
                slidesPerView: 4.5,
              },
              768: {
                spaceBetween: 10,
                slidesPerView: 5.5,
              },
              1024: {
                navigation: {
                  enabled: true,
                },
                slidesPerView: 3.5,
              },
              1280: {
                navigation: {
                  enabled: true,
                },
                slidesPerView: 4.5,
              },
            }}
            className="!p-1"
            resistanceRatio={0.5}
            shortSwipes={false}
            freeMode={true}
          >
            {[
              ...images,
              loadingReview
                ? [...Array.from({ length: 5 }).map(() => ({} as ReviewItem))]
                : [],
            ]
              .flat()
              .map((url, index) => {
                return (
                  <SwiperSlide key={index}>
                    <div
                      className="h-48 cursor-pointer"
                      onClick={() => {
                        setOpen(true);
                        setInitialSlide(index);
                      }}
                    >
                      {isEmpty(url) && loadingReview ? (
                        <Skeleton
                          className="rounded-lg w-full h-full"
                          containerClassName="w-full h-full"
                        />
                      ) : (
                        <img
                          src={url as string}
                          alt=""
                          className="rounded-lg w-full h-full object-cover"
                        />
                      )}
                    </div>
                  </SwiperSlide>
                );
              })}
          </Swiper>
          {!!images?.length && (
            <ModalCarousel
              initialSlide={initialSlide}
              images={images}
              items={items}
              onClose={() => setOpen(false)}
              open={open}
            />
          )}
        </div>
      ) : null}
    </div>
  );
};

export default ImagesReviewSlider;
